/**
 * Messages HTTP service (PX-W6.2) — the org thread's REST verbs.
 *
 * ⚠ THE LIST CONTRACT (verified against MessageController.list): the server
 * 400s `message.contextType.required` / `message.contextId.required` on BLANK
 * context params — and the route defaults are empty strings — so a caller that
 * omits them is refused, not defaulted. `list` therefore TAKES the context
 * tuple as required arguments; the org thread passes
 * `{ contextType: 'orgId', contextId: <activeOrgId> }`.
 *
 * Live append rides the SSE stream route (`GET /messages/stream`), which this
 * ticket does NOT wire (out of scope); reads are list-based.
 */

import { apiClient } from '@/services'

import type { ApiMessage, MessageCreateInput, MessagePage } from './types'

export const messagesHttp = {
  /**
   * One context's thread page. `contextType` and `contextId` are REQUIRED —
   * blank values are a 400, never a default.
   */
  list(params: {
    contextType: string
    contextId: string
    afterCursor?: string
    limit?: number
  }): Promise<MessagePage> {
    return apiClient.get<MessagePage>('/{orgId}/messages', { params })
  },

  /**
   * Insert a message. The body's `contextIds` must be NON-EMPTY (the service
   * 400s `message.contextIds.required` for every subtype — the OPPOSITE of
   * the activity contract); the org thread posts one `orgId` ref.
   */
  create(body: MessageCreateInput): Promise<ApiMessage> {
    return apiClient.post<ApiMessage>('/{orgId}/messages', body)
  },

  patch(id: string, body: { content: string }): Promise<ApiMessage> {
    return apiClient.patch<ApiMessage>(`/{orgId}/messages/${id}`, body)
  },

  remove(id: string): Promise<void> {
    return apiClient.delete<void>(`/{orgId}/messages/${id}`)
  }
}
